import React, { useState, useEffect } from 'react';
import { X, FileText, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoiceModal = ({ isOpen, onClose, leadId, agreementId, onSuccess }) => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [processConsultants, setProcessConsultants] = useState([]);
  
  const [formData, setFormData] = useState({
    agreement_id: agreementId || '',
    invoice_type: 'tax',
    billing_name: '',
    billing_address: '',
    billing_gstin: '',
    due_date: '',
    terms_and_conditions: '',
    is_partial: false,
    partial_amount: '',
    process_consultant: ''
  });

  useEffect(() => {
    if (isOpen && agreementId) {
      fetchAgreementDetails();
      fetchExistingInvoices();
      fetchProcessConsultants();
    }
  }, [isOpen, agreementId]);

  const fetchProcessConsultants = async () => {
    try {
      const response = await axios.get(
        `${API}/users/process-consultants`,
        { headers: getAuthHeader() }
      );
      setProcessConsultants(response.data);
    } catch (error) {
      console.error('Failed to fetch process consultants:', error);
    }
  };

  const fetchAgreementDetails = async () => {
    try {
      const response = await axios.get(
        `${API}/agreements/${agreementId}`,
        { headers: getAuthHeader() }
      );
      setAgreement(response.data);
      
      // Pre-fill form
      setFormData(prev => ({
        ...prev,
        agreement_id: agreementId,
        billing_name: response.data.lead_name || '',
        due_date: getDefaultDueDate()
      }));
    } catch (error) {
      console.error('Failed to fetch agreement:', error);
    }
  };

  const fetchExistingInvoices = async () => {
    try {
      const response = await axios.get(
        `${API}/invoices?lead_id=${leadId}`,
        { headers: getAuthHeader() }
      );
      
      // Filter invoices for this agreement
      const agreementInvoices = response.data.filter(
        inv => inv.agreement_id === agreementId
      );
      setExistingInvoices(agreementInvoices);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  const calculateRemainingAmount = () => {
    if (!agreement) return 0;
    
    const totalAgreementAmount = agreement.total_amount || 0;
    const totalInvoiced = existingInvoices.reduce(
      (sum, inv) => sum + (inv.total_amount || 0),
      0
    );
    
    return totalAgreementAmount - totalInvoiced;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.is_partial) {
      const partialAmount = parseFloat(formData.partial_amount);
      const remaining = calculateRemainingAmount();
      
      if (!partialAmount || partialAmount <= 0) {
        alert('Please enter a valid partial amount');
        return;
      }
      
      if (partialAmount > remaining) {
        alert(`Partial amount cannot exceed remaining amount (₹${remaining.toFixed(2)})`);
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        partial_amount: formData.is_partial ? parseFloat(formData.partial_amount) : null
      };
      
      await axios.post(
        `${API}/invoices`,
        payload,
        { headers: getAuthHeader() }
      );
      
      alert('Invoice created successfully!');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create invoice';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const remainingAmount = calculateRemainingAmount();
  const totalInvoiced = agreement ? 
    existingInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) : 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <div className="modal-title-with-icon">
            <FileText size={24} style={{ color: '#777db0' }} />
            <h2>Generate Invoice</h2>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {agreement && (
            <div style={{
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #dee2e6'
            }}>
              <h3 style={{ fontSize: '14px', marginBottom: '10px', color: '#495057' }}>
                Agreement Summary
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                <div>
                  <strong>Agreement:</strong> {agreement.agreement_number}
                </div>
                <div>
                  <strong>Total Amount:</strong> ₹{agreement.total_amount?.toLocaleString()}
                </div>
                <div>
                  <strong>Already Invoiced:</strong> ₹{totalInvoiced.toLocaleString()}
                </div>
                <div>
                  <strong>Remaining:</strong> ₹{remainingAmount.toLocaleString()}
                </div>
              </div>
              
              {existingInvoices.length > 0 && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #dee2e6' }}>
                  <strong style={{ fontSize: '12px' }}>Existing Invoices:</strong>
                  <div style={{ fontSize: '12px', marginTop: '5px' }}>
                    {existingInvoices.map((inv, idx) => (
                      <div key={idx} style={{ marginBottom: '3px' }}>
                        • {inv.invoice_number} - ₹{inv.total_amount?.toLocaleString()} ({inv.payment_status})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Invoice Type */}
            <div className="form-group">
              <label className="form-label">Invoice Type</label>
              <div style={{ display: 'flex', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="invoice_type"
                    value="tax"
                    checked={formData.invoice_type === 'tax'}
                    onChange={(e) => setFormData({ ...formData, invoice_type: e.target.value })}
                    style={{ marginRight: '8px' }}
                  />
                  Tax Invoice (with GST)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="invoice_type"
                    value="cash"
                    checked={formData.invoice_type === 'cash'}
                    onChange={(e) => setFormData({ ...formData, invoice_type: e.target.value })}
                    style={{ marginRight: '8px' }}
                  />
                  Cash Invoice
                </label>
              </div>
            </div>

            {/* Partial Invoice Toggle */}
            {remainingAmount < (agreement?.total_amount || 0) && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_partial}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      is_partial: e.target.checked,
                      partial_amount: ''
                    })}
                    style={{ marginRight: '10px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Generate Partial Invoice
                  </span>
                </label>
              </div>
            )}

            {formData.is_partial && (
              <div className="form-group">
                <label className="form-label">Partial Amount (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.partial_amount}
                  onChange={(e) => setFormData({ ...formData, partial_amount: e.target.value })}
                  placeholder={`Max: ₹${remainingAmount.toFixed(2)}`}
                  required={formData.is_partial}
                />
                <p className="caption" style={{ marginTop: '5px', color: '#6c757d' }}>
                  Remaining amount: ₹{remainingAmount.toLocaleString()}
                </p>
              </div>
            )}

            {/* Billing Details */}
            <div className="form-group">
              <label className="form-label">Billing Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.billing_name}
                onChange={(e) => setFormData({ ...formData, billing_name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Billing Address</label>
              <textarea
                className="form-input"
                value={formData.billing_address}
                onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                rows={3}
                placeholder="Enter complete billing address"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Client GSTIN (if applicable)</label>
              <input
                type="text"
                className="form-input"
                value={formData.billing_gstin}
                onChange={(e) => setFormData({ ...formData, billing_gstin: e.target.value })}
                placeholder="Enter GST number"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Assign Process Consultant *</label>
              <select
                className="form-input"
                value={formData.process_consultant}
                onChange={(e) => setFormData({ ...formData, process_consultant: e.target.value })}
                required
              >
                <option value="">Select Process Consultant</option>
                {processConsultants.map(consultant => (
                  <option key={consultant.email} value={consultant.email}>
                    {consultant.name} ({consultant.role})
                  </option>
                ))}
              </select>
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                This consultant will handle the client's onboarding and the lead will be reassigned to them
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Due Date *</label>
              <input
                type="date"
                className="form-input"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Terms & Conditions</label>
              <textarea
                className="form-input"
                value={formData.terms_and_conditions}
                onChange={(e) => setFormData({ ...formData, terms_and_conditions: e.target.value })}
                rows={4}
                placeholder="Payment terms, bank details, etc."
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Generating...' : 'Generate Invoice'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
